/**
 * SwapAnalyticsPage
 *
 * Dedicated page for swap analytics and history.
 * Includes:
 * - Swap statistics and performance metrics
 * - Complete swap history with filtering
 * - Export functionality for tax purposes
 *
 * Only available in expert mode.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { SwapAnalytics } from '../components/portfolio/SwapAnalytics';
import { SwapHistory } from '../components/portfolio/SwapHistory';
import { SwapExport } from '../components/portfolio/SwapExport';
import { usePortfolio } from '../hooks/usePortfolio';
import { usePositions } from '../hooks/usePositions';
import { useCoinPrices } from '../hooks/queries/useCoinGecko';
import { PortfolioManager } from '../services/portfolioManager';
import type { Token, Position, Prices } from '../types';
import type { UserMode } from '../hooks/useUserMode';

interface SwapAnalyticsPageProps {
  userMode?: UserMode;
}

export function SwapAnalyticsPage({ userMode = 'expert' }: SwapAnalyticsPageProps) {
  const { t } = useTranslation();
  const [swapExportModalOpen, setSwapExportModalOpen] = useState(false);
  const [tokens, setTokens] = useState<Token[]>([]);

  // Load portfolio data
  const portfolioHook = usePortfolio();
  const positionsHook = usePositions(portfolioHook.activePortfolio?.id);

  // Get token IDs for price fetching
  const tokenIds = tokens.map(t => t.id);
  const { data: prices = {} } = useCoinPrices(tokenIds, {
    enabled: tokenIds.length > 0,
    refetchInterval: 60000,
  });

  // Sync tokens from portfolio hook
  useEffect(() => {
    if (portfolioHook.tokens.length > 0 || positionsHook.positions.length > 0) {
      // Group positions by token
      const positionsByToken = new Map<string, Position[]>();

      positionsHook.positions.forEach((hybridPos) => {
        const tokenId = 'token_id' in hybridPos ? hybridPos.token_id : hybridPos.tokenId;

        const legacyPosition: Position = {
          id: hybridPos.id,
          type: hybridPos.type,
          date: hybridPos.date,
          quantity: typeof hybridPos.quantity === 'string' ? parseFloat(hybridPos.quantity) : hybridPos.quantity,
          price: typeof hybridPos.price === 'string' ? parseFloat(hybridPos.price) : hybridPos.price,
          note: hybridPos.note || undefined,
          is_swap: (hybridPos as any).is_swap,
          swap_chain_id: (hybridPos as any).swap_chain_id,
          original_cost_basis: (hybridPos as any).original_cost_basis,
          source_chain: (hybridPos as any).source_chain,
          destination_chain: (hybridPos as any).destination_chain,
          bridge_protocol: (hybridPos as any).bridge_protocol,
          bridge_fee: (hybridPos as any).bridge_fee,
          gas_fee_usd: (hybridPos as any).gas_fee_usd,
          swap_platform: (hybridPos as any).swap_platform,
          bridge_tx_hash: (hybridPos as any).bridge_tx_hash,
        };

        const existing = positionsByToken.get(tokenId) || [];
        positionsByToken.set(tokenId, [...existing, legacyPosition]);
      });

      // Convert to legacy Token format
      let legacyTokens: Token[] = portfolioHook.tokens.map((hybridToken) => {
        const tokenId = 'coin_id' in hybridToken ? hybridToken.coin_id : hybridToken.id;
        const displayName = 'symbol' in hybridToken ? hybridToken.symbol : 'name' in hybridToken ? hybridToken.name : tokenId;

        return {
          id: tokenId,
          displayName: (displayName || tokenId || 'UNKNOWN').toUpperCase(),
          positions: positionsByToken.get(tokenId) || [],
        };
      });

      // Fallback to PortfolioManager if needed
      if (legacyTokens.length === 0 && positionsHook.positions.length > 0 && portfolioHook.activePortfolio) {
        const pmPortfolio = PortfolioManager.getPortfolio(portfolioHook.activePortfolio.id);
        if (pmPortfolio && pmPortfolio.tokens && pmPortfolio.tokens.length > 0) {
          legacyTokens = pmPortfolio.tokens.map(token => ({
            id: token.id,
            displayName: (token.displayName || token.id || 'UNKNOWN').toUpperCase(),
            positions: positionsByToken.get(token.id) || token.positions || [],
          }));
        }
      }

      if (legacyTokens.length > 0) {
        setTokens(legacyTokens);
      }
    }
  }, [portfolioHook.tokens, positionsHook.positions, portfolioHook.activePortfolio]);

  // Count swaps
  const swapCount = tokens.reduce((count, token) => {
    return count + token.positions.filter(p => p.is_swap === true).length;
  }, 0);

  // If no swaps, show empty state
  if (swapCount === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '20px',
        padding: '40px',
        textAlign: 'center',
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.2) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icons.ArrowRightLeft size={40} style={{ color: 'var(--accent)' }} />
        </div>
        <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>
          {t('swapAnalyticsPage.noSwaps', 'Aucun Swap Enregistré')}
        </h2>
        <p style={{ color: 'var(--muted)', maxWidth: '400px', lineHeight: 1.6 }}>
          {t('swapAnalyticsPage.noSwapsDesc', 'Effectuez des swaps depuis votre portfolio pour voir apparaître les statistiques et l\'historique ici.')}
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 20px 40px 20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{
        marginBottom: '24px',
        paddingBottom: '20px',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, var(--accent) 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icons.ArrowRightLeft size={24} style={{ color: 'white' }} />
          </div>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>
              {t('swapAnalyticsPage.title', 'Swap Analytics')}
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
              {t('swapAnalyticsPage.subtitle', 'Statistiques détaillées et historique de vos opérations de swap')}
            </p>
          </div>
        </div>
      </div>

      {/* Swap Analytics Dashboard */}
      <SwapAnalytics
        tokens={tokens}
        prices={prices}
        isExpertMode={userMode === 'expert'}
      />

      {/* Swap History */}
      <div style={{ marginTop: '20px' }}>
        <SwapHistory
          tokens={tokens}
          prices={prices}
          isExpertMode={userMode === 'expert'}
          onExport={() => setSwapExportModalOpen(true)}
        />
      </div>

      {/* Swap Export Modal */}
      <SwapExport
        tokens={tokens}
        prices={prices}
        isOpen={swapExportModalOpen}
        onClose={() => setSwapExportModalOpen(false)}
      />
    </div>
  );
}

export default SwapAnalyticsPage;
